import type { InputPremise } from "./Premises";

export type PremiseLayout = "card" | "list";

export type OriginGroupKey = "erp" | "integrations" | "mass_load" | "config" | "other";

export type OriginGroup = {
  key: OriginGroupKey;
  title: string;
  hint: string;
  origins: string[];
};

export const ORIGIN_GROUPS: OriginGroup[] = [
  {
    key: "erp",
    title: "ERP Profit",
    hint: "Datos sincronizados desde la base de datos del ERP",
    origins: ["erp_database"],
  },
  {
    key: "integrations",
    title: "Integraciones",
    hint: "Fuentes externas conectadas al simulador",
    origins: ["monday_api"],
  },
  {
    key: "mass_load",
    title: "Cargas masivas",
    hint: "Archivos y plantillas cargados por el equipo",
    origins: ["mass_load"],
  },
  {
    key: "config",
    title: "Configuración",
    hint: "Parámetros definidos en sesión o de forma global",
    origins: ["user_input"],
  },
];

export function getOriginLabel(origin: string): string {
  if (origin === "mass_load") return "Carga masiva";
  if (origin === "erp_database") return "Base de datos Profit";
  if (origin === "monday_api") return "API de Monday";
  if (origin === "user_input") return "Configuración";
  return origin;
}

export function getOriginBadgeColor(origin: string): string {
  if (origin === "erp_database") return "purple";
  if (origin === "monday_api") return "pink";
  if (origin === "mass_load") return "teal";
  if (origin === "user_input") return "orange";
  return "gray";
}

export function formatPremiseMetric(premise: InputPremise): string {
  const { value, summarizingString, itemCount } = premise;
  if (itemCount != null && summarizingString) {
    return `${Number(itemCount).toLocaleString("es-VE")} ${summarizingString}`;
  }
  if (itemCount != null) {
    return Number(itemCount).toLocaleString("es-VE");
  }
  const valueLabel =
    value === "all" ? "Todos" : value != null && value !== "" ? String(value) : null;
  if (valueLabel && summarizingString) {
    return `${valueLabel} ${summarizingString}`;
  }
  if (valueLabel) return valueLabel;
  return "Sin configurar";
}

export function isPremiseConfigured(premise: InputPremise): boolean {
  const { value, itemCount } = premise;
  if (itemCount != null && itemCount > 0) return true;
  if (value == null || value === "") return false;
  if (value === "all") return true;
  return true;
}

export function getSessionTypeLabel(type: string): string {
  if (type === "session") return "Sesión";
  if (type === "global") return "Global";
  if (type === "external") return "Externo";
  return type;
}

/** Premisas ocultas en el drawer de premisas del simulador. */
export const HIDDEN_PREMISE_NAMES = new Set([
  "products_master",
  "products_forecast",
]);

export function filterPremisesForSimulatorDrawer(
  premises: InputPremise[],
): InputPremise[] {
  return premises.filter((p) => !HIDDEN_PREMISE_NAMES.has(p.name));
}

export function groupPremisesByOrigin(
  premises: InputPremise[]
): { group: OriginGroup; items: InputPremise[] }[] {
  const assigned = new Set<number>();
  const buckets: { group: OriginGroup; items: InputPremise[] }[] = [];

  for (const group of ORIGIN_GROUPS) {
    const items = premises.filter((p) => {
      if (assigned.has(p.id)) return false;
      if (!group.origins.includes(p.origin)) return false;
      assigned.add(p.id);
      return true;
    });
    if (items.length > 0) buckets.push({ group, items });
  }

  const other = premises.filter((p) => !assigned.has(p.id));
  if (other.length > 0) {
    buckets.push({
      group: {
        key: "other",
        title: "Otros",
        hint: "Parámetros adicionales del simulador",
        origins: [],
      },
      items: other,
    });
  }

  return buckets;
}
